import moment from "moment";
import React from "react";
import { Link } from "react-router-dom";
import {
  getChangeKindLabel,
  getChangeReviewGuidance,
  isDetailsChange,
  isOpenTicketChange,
} from "../../../../data/flight/order/getChangeAdminGuidance";
import {
  getRequestedDateChangeSummary,
  getOriginalRouteSummary,
  getRequestedRouteSummary,
  isRouteChangeRequest,
  getChangeRequestPassengers,
  getScopedItineraryLegs,
} from "../../../../data/flight/order/getRequestedDateChange";

const TYPE_LABEL = {
  ADT: "Adult",
  Adult: "Adult",
  CHD: "Child",
  CNN: "Child",
  Child: "Child",
  INF: "Infant",
  INS: "Infant",
  Infant: "Infant",
};

export function normalizeChangeOrder(data) {
  if (!data) return data;
  const booking = Array.isArray(data.flightBooking)
    ? data.flightBooking[0]
    : data.flightBooking || data.flightObj;
  return { ...data, flightBooking: booking };
}

function Row({ label, value, note, warn }) {
  if (!value && !note) return null;
  return (
    <div className="grid grid-cols-[110px_1fr] sm:grid-cols-[140px_1fr] gap-2 py-2 border-b border-gray-100 last:border-b-0">
      <div className="text-xs text-gray-500 pt-0.5">{label}</div>
      <div className="flex flex-col gap-0.5 min-w-0">
        {value ? (
          <div className="text-sm text-gray-900 break-words">{value}</div>
        ) : null}
        {note ? (
          <div
            className={`text-xs ${warn ? "text-amber-700" : "text-gray-500"}`}
          >
            {note}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function makeReadableDocName(path) {
  if (!path) return "Document";
  const url = typeof path === "string" ? path : path?.url || path?.name || "";
  if (!url) return "Document";
  let name = decodeURIComponent(String(url).split("/").pop() || "Document");
  name = name.replace(/-\d+\.(pdf|docx|txt|csv|png|jpe?g|gif|webp)$/i, ".$1");
  name = name.replace(/(\.[a-z0-9]+)\1$/i, "$1");
  name = name.replace(/[_-]+/g, " ");
  return name.charAt(0).toUpperCase() + name.slice(1);
}

function getRelatedDocs(order) {
  const raw = order?.relatedDocs || order?.relatedDocuments || [];
  if (!Array.isArray(raw)) return [];
  return raw
    .map((doc) => {
      if (typeof doc === "string") return { url: doc, name: makeReadableDocName(doc) };
      if (doc && typeof doc === "object") {
        const url = doc.url || doc.path || doc.href || "";
        if (!url) return null;
        return {
          url,
          name: doc.name || doc.fileName || makeReadableDocName(url),
        };
      }
      return null;
    })
    .filter(Boolean);
}

function RelatedDocsRow({ docs }) {
  const [busyUrl, setBusyUrl] = React.useState("");

  async function handleDownload(doc) {
    if (!doc?.url || busyUrl) return;
    setBusyUrl(doc.url);
    try {
      const res = await fetch(doc.url, { mode: "cors" });
      if (!res.ok) throw new Error("fetch failed");
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = doc.name || "document";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (_) {
      // Cross-origin without CORS still opens the file so staff can save it.
      const link = document.createElement("a");
      link.href = doc.url;
      link.download = doc.name || "document";
      link.rel = "noopener";
      document.body.appendChild(link);
      link.click();
      link.remove();
    } finally {
      setBusyUrl("");
    }
  }

  if (!docs?.length) return null;
  return (
    <div className="grid grid-cols-[110px_1fr] sm:grid-cols-[140px_1fr] gap-2 py-2 border-b border-gray-100 last:border-b-0">
      <div className="text-xs text-gray-500 pt-0.5">Documents</div>
      <div className="flex flex-col gap-1.5 min-w-0">
        {docs.map((doc, index) => (
          <div
            key={`${doc.url}-${index}`}
            className="flex items-center justify-between gap-2 rounded-lg border border-gray-100 bg-gray-50 px-2.5 py-1.5"
          >
            <span className="text-sm text-gray-900 truncate" title={doc.name}>
              {doc.name}
            </span>
            <button
              type="button"
              disabled={busyUrl === doc.url}
              onClick={() => handleDownload(doc)}
              className="shrink-0 text-xs font-semibold text-primary1 underline hover:opacity-80 disabled:opacity-50"
            >
              {busyUrl === doc.url ? "…" : "Download"}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function personName(person) {
  return [person?.firstName, person?.middleName, person?.lastName]
    .filter(Boolean)
    .join(" ");
}

function formatPerson(person) {
  const type =
    TYPE_LABEL[person?.travelerType] ||
    TYPE_LABEL[person?.type] ||
    person?.travelerType ||
    person?.type ||
    "";
  const name = personName(person);
  if (!name) return type || "-";
  return type ? `${name} (${type})` : name;
}

function formatDate(raw) {
  if (!raw) return "-";
  const parsed = moment(raw);
  return parsed.isValid() ? parsed.format("D MMM YYYY") : String(raw);
}

function getTicketNumbers(travelers) {
  return [
    ...new Set(
      (travelers || []).flatMap((t) => t?.ticketNumbers || []).filter(Boolean),
    ),
  ];
}

function getScopedTravelers(order) {
  const travelers = order?.flightBooking?.travelers || [];
  const info = Array.isArray(order?.passengerInfo) ? order.passengerInfo : [];
  if (!info.length) return travelers;
  const ids = new Set(info.map((p) => String(p?.id)).filter(Boolean));
  const matched = travelers.filter((t) => ids.has(String(t?.id)));
  return matched.length ? matched : travelers;
}

function getRoute(data) {
  const flights = data?.flightBooking?.flights || [];
  if (flights.length) {
    const first = flights[0];
    const last = flights[flights.length - 1];
    const from = first?.departureLocation || first?.departure?.location;
    const to = last?.arrivalLocation || last?.arrival?.location;
    if (from && to) return `${from} → ${to}`;
  }
  const { legs } = getScopedItineraryLegs(data);
  if (!legs.length) return "-";
  const outbound = legs.filter((leg) => String(leg?.segment) === "1");
  const routeLegs = outbound.length ? outbound : legs;
  const from = routeLegs[0]?.departureLocation;
  const to = routeLegs.at(-1)?.arrivalLocation;
  if (!from || !to) return "-";
  return `${from} → ${to}`;
}

function groupItineraryLegs(legs) {
  const groups = [];
  for (const leg of legs || []) {
    const key = String(leg?.segment || groups.length + 1);
    let group = groups.find((item) => item.key === key);
    if (!group) {
      group = { key, legs: [] };
      groups.push(group);
    }
    group.legs.push(leg);
  }
  return groups;
}

function directionLabel(group, index, total) {
  if (total <= 1) return null;
  if (group.key === "1" || index === 0) return "Outbound";
  if (group.key === "2" || index === 1) return "Return";
  return `Segment ${group.key}`;
}

function OriginalTrip({ order }) {
  const { legs, allLegs, partial } = getScopedItineraryLegs(order);
  if (!legs.length) {
    return (
      <div className="text-sm text-amber-700">
        No original itinerary listed. Confirm the flights before you continue.
      </div>
    );
  }

  const groups = groupItineraryLegs(legs);
  const totalDirections = new Set(
    allLegs.map((leg) => String(leg?.segment)).filter(Boolean),
  ).size;

  return (
    <div className="flex flex-col gap-3">
      {partial ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          Opening {groups.length} of {totalDirections} directions only. Other
          flights stay as booked.
        </div>
      ) : null}
      {groups.map((group, index) => {
        const title = directionLabel(
          group,
          index,
          totalDirections > 1 ? totalDirections : groups.length,
        );
        const from = group.legs[0]?.departureLocation;
        const to = group.legs.at(-1)?.arrivalLocation;
        return (
          <div key={group.key} className="flex flex-col gap-2">
            {title || (from && to) ? (
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                {title || "Direction"}
                {from && to ? ` · ${from} → ${to}` : ""}
              </div>
            ) : null}
            <div className="flex flex-col gap-2">
              {group.legs.map((leg, legIndex) => {
                const route = [leg?.departureLocation, leg?.arrivalLocation]
                  .filter(Boolean)
                  .join(" → ");
                const cities = [
                  leg?.departureLocationName,
                  leg?.arrivalLocationName,
                ]
                  .filter(Boolean)
                  .join(" to ");
                const flight = [leg?.airlineName || leg?.airline, leg?.flightNumber]
                  .filter(Boolean)
                  .join(" ");
                const times = [leg?.departureTime, leg?.arrivalTime]
                  .filter(Boolean)
                  .join(" – ");
                return (
                  <div
                    key={leg?._id || `${group.key}-${legIndex}`}
                    className="rounded-lg border border-gray-200 bg-white px-3 py-2"
                  >
                    <div className="text-sm font-semibold text-gray-900">
                      {route || "Flight"}
                    </div>
                    {cities ? (
                      <div className="text-xs text-gray-500">{cities}</div>
                    ) : null}
                    <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-gray-600">
                      {flight ? <span>{flight}</span> : null}
                      {leg?.departureDate ? (
                        <span>{formatDate(leg.departureDate)}</span>
                      ) : null}
                      {times ? <span>{times}</span> : null}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
      <div className="text-xs text-gray-500">
        {partial
          ? "The passenger is not flying this direction. If it can be opened, cancel these flights and keep the ticket OPEN. Leave the other direction alone."
          : "The passenger is not flying this trip. If it can be opened, cancel these flights and keep the ticket OPEN."}
      </div>
    </div>
  );
}

function getPassengerChanges(order) {
  const oldTravelers = order?.flightBooking?.travelers || [];
  const requested = order?.passengerInfo || [];
  if (!requested.length) {
    return { line: "No passenger changes listed", warn: true };
  }

  const lines = requested.map((person) => {
    const previous =
      oldTravelers.find((traveler) => traveler?.id === person?.id) || {};
    const oldName = personName(previous);
    const newName = personName(person);
    if (oldName && newName && oldName !== newName) {
      return `${oldName} → ${newName}`;
    }
    return formatPerson(person);
  });

  return { line: lines.join("; "), warn: false };
}

function getDateChanges(order) {
  return getRequestedDateChangeSummary(order);
}

export function ChangeReviewFacts({ data, showQuestions = true }) {
  const order = normalizeChangeOrder(data) || {};
  const booking = order?.flightBooking || {};
  const agent = order?.account || {};
  const isDetails = isDetailsChange(order);
  const isOpenTicket = isOpenTicketChange(order);
  const isRouteChange = !isDetails && !isOpenTicket && isRouteChangeRequest(order);
  const kind = getChangeKindLabel(order);
  const travelers = getScopedTravelers(order);
  const tickets = getTicketNumbers(travelers);
  const bookingStatus = booking?.status || "-";
  const issued =
    String(bookingStatus).toLowerCase() === "issued" || tickets.length > 0;
  const bookingId = booking?.bookingId || order?.bookingId;
  const passengerChanges = getPassengerChanges(order);
  const openTicketPassengers = getChangeRequestPassengers(order, booking);
  const dateChanges = getDateChanges(order);
  const originalRoute = getOriginalRouteSummary(order);
  const requestedRoute = getRequestedRouteSummary(order);
  const relatedDocs = getRelatedDocs(order);

  return (
    <div className="flex flex-col">
      <div className="rounded-lg border border-gray-100 bg-[#F8FAFC] px-3">
        <Row
          label="Ticket"
          value={
            tickets.length
              ? `${bookingStatus} · ${tickets.join(" | ")}`
              : bookingStatus
          }
          note={
            issued
              ? null
              : "No ticket number found. Confirm this booking was issued before you continue."
          }
          warn={!issued}
        />
        {isOpenTicket ? (
          <>
            <Row
              label="Passengers"
              value={
                openTicketPassengers.length
                  ? openTicketPassengers.join(", ")
                  : "No passengers listed"
              }
              note={
                Array.isArray(order?.passengerInfo) &&
                order.passengerInfo.length
                  ? order.passengerInfo.length === 1
                    ? "1 passenger on this open — not the whole booking"
                    : `${order.passengerInfo.length} passengers on this open`
                  : "Everyone on the booking is in scope"
              }
              warn={!openTicketPassengers.length}
            />
            <Row
              label="Original trip"
              value={<OriginalTrip order={order} />}
            />
          </>
        ) : isDetails ? (
          <Row
            label="Name change"
            value={passengerChanges.line}
            note={
              passengerChanges.warn
                ? "Nobody is listed. Do not accept until you know who is changing."
                : "This is the requested passenger update. Accepting is not a quote."
            }
            warn={passengerChanges.warn}
          />
        ) : isRouteChange ? (
          <Row
            label="Route change"
            value={
              dateChanges ||
              (originalRoute && requestedRoute
                ? `${originalRoute} → ${requestedRoute}`
                : requestedRoute || "Route change requested")
            }
            note={
              originalRoute && requestedRoute
                ? `Original ${originalRoute}. Accepting is not a quote — check the new cities before you continue.`
                : "Origin or destination changed. Accepting is not a quote."
            }
            warn={!dateChanges && !requestedRoute}
          />
        ) : (
          <Row
            label="Date change"
            value={dateChanges || "No new dates listed"}
            note={
              dateChanges
                ? "This is the requested new travel date. Accepting is not a quote."
                : "No new dates listed. Do not accept until you know what is changing."
            }
            warn={!dateChanges}
          />
        )}
        {isOpenTicket ? (
          <Row label="PNR" value={booking?.pnr || order?.bookingId || "-"} />
        ) : (
          <Row
            label="Flight"
            value={getRoute(order)}
            note={booking?.pnr ? `PNR ${booking.pnr}` : null}
          />
        )}
        <Row label="Reason" value={order?.reason || "-"} note={order?.remark || null} />
        <RelatedDocsRow docs={relatedDocs} />
        <Row
          label="Agent"
          value={
            [agent?.firstName, agent?.lastName].filter(Boolean).join(" ") || "-"
          }
          note={agent?.email || agent?.phone || null}
        />
      </div>

      {bookingId ? (
        <Link
          to={`/order/flight/ticket/detail/${bookingId}?page=Bookings`}
          target="_blank"
          rel="noreferrer"
          className="inline-block mt-3 text-sm font-medium text-primary1 hover:underline"
        >
          Open original booking
        </Link>
      ) : null}

      {showQuestions ? (
        <div className="mt-4 text-sm text-gray-700">
          <div className="font-medium text-gray-900 mb-1">Ask yourself</div>
          <ol className="list-decimal pl-5 space-y-1">
            <li>Is this the right issued ticket?</li>
            <li>
              {isOpenTicket
                ? "Can this ticket be opened, or is it not eligible?"
                : isDetails
                  ? "Are the new names allowed on this ticket?"
                  : "Are the new dates possible for this fare?"}
            </li>
            <li>
              {isOpenTicket
                ? "There is no quote and no charge. Should Ops take this to the airline, or mark it not eligible?"
                : `Should Intraverse take this ${kind}, or should it be declined?`}
            </li>
          </ol>
        </div>
      ) : null}
    </div>
  );
}

export default function ChangeReviewCard({ data }) {
  if (!data) return null;
  const review = getChangeReviewGuidance(data);

  return (
    <div className="w-full rounded-xl mt-3 border border-gray-200 bg-white px-4 py-4">
      <div className="mb-3">
        <h3 className="text-base font-semibold text-gray-900">{review.title}</h3>
        {review.statusDisplay ? (
          <p className="text-xs font-medium text-gray-500 mt-1">
            Status: {review.statusDisplay}
          </p>
        ) : null}
        <p className="text-sm text-gray-600 mt-1">{review.why}</p>
      </div>
      <div className="mb-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-3">
        <div className="text-sm font-semibold text-amber-900">Do this</div>
        <ol className="mt-1 list-decimal pl-5 space-y-1 text-sm text-gray-800">
          {review.steps.map((step) => (
            <li key={step}>{step}</li>
          ))}
        </ol>
      </div>
      <ChangeReviewFacts data={data} showQuestions={false} />
    </div>
  );
}
